import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Plus, Search, Calendar, MapPin, Clock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { eventsApi, Event } from '@/lib/api'
import { formatDate, formatNumber, getInitials } from '@/lib/utils'

export function EventsPage() {
  const { eventId } = useParams()

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getEvents(),
  })

  const events = eventsData?.data?.data || []

  if (eventId) {
    return <EventView eventId={eventId} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Events</h1>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Create Event
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search events..." className="pl-9" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 font-display text-lg font-semibold">No events yet</h3>
            <p className="mt-2 text-muted-foreground">
              Create or discover events happening near you
            </p>
            <Button className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Create Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event: Event, index: number) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <EventCard event={event} />
            </motion.div>
          ))}
        </div>
      )}

      {/* Upcoming events */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upcoming Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {['Summer Party', 'Music Festival', 'Art Exhibition'].map((name, i) => (
              <div key={name} className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <span className="text-xs font-medium">JAN</span>
                  <span className="text-lg font-bold">{15 + i}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium">{name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(10 - i) * 12} going
                  </p>
                </div>
                <Button size="sm" variant="outline">
                  Interested
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function EventCard({ event }: { event: Event }) {
  const eventDate = new Date(event.startTime)

  return (
    <a href={`/events/${event.id}`}>
      <Card className="overflow-hidden transition-colors hover:border-primary/50">
        <div className="flex">
          {event.coverUrl ? (
            <div className="h-32 w-32 flex-shrink-0">
              <img
                src={event.coverUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="flex h-32 w-32 flex-shrink-0 flex-col items-center justify-center bg-primary/10 text-primary">
              <span className="text-sm font-medium">
                {eventDate.toLocaleDateString('en-US', { month: 'short' })}
              </span>
              <span className="text-3xl font-bold">{eventDate.getDate()}</span>
            </div>
          )}

          <CardContent className="flex flex-1 flex-col justify-center py-4">
            <h3 className="font-medium">{event.name}</h3>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                {eventDate.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </div>
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  {event.location}
                </div>
              )}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {formatNumber(event.attendeesCount)} going
            </p>
          </CardContent>
        </div>
      </Card>
    </a>
  )
}

function EventView({ eventId }: { eventId: string }) {
  const { data: eventData, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventsApi.getEvent(eventId),
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const event = eventData?.data

  if (!event) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <h3 className="font-display text-lg font-semibold">Event not found</h3>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="h-48 bg-gradient-to-r from-primary/30 to-accent/30 sm:h-64">
          {event.coverUrl && (
            <img src={event.coverUrl} alt="" className="h-full w-full object-cover" />
          )}
        </div>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold">{event.name}</h1>
              <div className="mt-2 space-y-1 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {formatDate(event.startTime)}
                </div>
                {event.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {event.location}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline">Interested</Button>
              <Button>Going</Button>
            </div>
          </div>
          <p className="mt-4">{event.description}</p>
        </CardContent>
      </Card>
    </div>
  )
}
